import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from "react";
import { useUser } from "./UserContext";
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db, dbNotes } from "../firebase";
import { Category, CategoryContextValue } from "../types";
import { v4 as uuidv4 } from "uuid";

const DEFAULT_CATEGORIES: Category[] = [
  { id: "note", name: "Note", color: "#95a5a6", icon: "fa-file-lines", order: 0, isDefault: true },
  { id: "movie", name: "Movie", color: "#e74c3c", icon: "fa-film", order: 1, isDefault: true },
  { id: "music", name: "Music", color: "#9b59b6", icon: "fa-music", order: 2, isDefault: true },
  { id: "school", name: "School", color: "#3498db", icon: "fa-graduation-cap", order: 3, isDefault: true },
  { id: "work", name: "Work", color: "#2ecc71", icon: "fa-briefcase", order: 4, isDefault: true },
  { id: "ideas", name: "Ideas", color: "#f39c12", icon: "fa-lightbulb", order: 5, isDefault: true },
  { id: "journal", name: "Journal", color: "#1abc9c", icon: "fa-book-open", order: 6, isDefault: true },
  { id: "recipe", name: "Recipe", color: "#e67e22", icon: "fa-utensils", order: 7, isDefault: true },
];

const CategoryContext = createContext<CategoryContextValue | null>(null);

export const CategoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userDetails } = useUser()!;
  const [categories, setCategories] = useState<Category[]>(DEFAULT_CATEGORIES);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (userDetails?.user?.uid) {
      loadCategories();
    } else {
      setIsLoading(false);
    }
  }, [userDetails?.user?.uid]);

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      const userId = userDetails?.user?.uid;
      if (!userId) return;

      const userDoc = await getDoc(doc(db, "users", userId));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userCategories = userData.customCategories as Category[] | undefined;

        if (userCategories && userCategories.length > 0) {
          setCategories(userCategories);
        } else {
          // First time user - initialize with defaults
          await initializeDefaultCategories(userId);
          setCategories(DEFAULT_CATEGORIES);
        }

        // Auto-migrate existing note categories (skip if already done for this user)
        const migrationKey = `onyxlife_categories_migrated_${userId}`;
        if (!localStorage.getItem(migrationKey)) {
          await migrateUserCategories(userId);
          localStorage.setItem(migrationKey, "true");
        }
      } else {
        // User document doesn't exist yet - use defaults
        setCategories(DEFAULT_CATEGORIES);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setCategories(DEFAULT_CATEGORIES);
    } finally {
      setIsLoading(false);
    }
  };

  const initializeDefaultCategories = async (userId: string) => {
    try {
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, {
        customCategories: DEFAULT_CATEGORIES
      });
    } catch (error) {
      console.error("Error initializing default categories:", error);
    }
  };

  const migrateUserCategories = async (userId: string) => {
    try {
      // Fetch all user's notes to find any legacy categories
      const notesQuery = query(collection(db, "notes"), where("userId", "==", userId));
      const notesSnapshot = await getDocs(notesQuery);

      const existingCategoryNames = new Set(categories.map(c => c.name.toLowerCase()));
      const existingCategoryIds = new Set(categories.map(c => c.id));
      const legacyCategories: Category[] = [];

      notesSnapshot.docs.forEach(doc => {
        const noteData = doc.data();
        const noteCategory = noteData.category as string | undefined;

        if (noteCategory) {
          const categoryLower = noteCategory.toLowerCase();
          // Check if this is not a category ID and not already in our category list
          if (!existingCategoryIds.has(noteCategory) &&
              !existingCategoryNames.has(categoryLower) &&
              !legacyCategories.some(c => c.name.toLowerCase() === categoryLower)) {
            // Create a legacy category for this
            legacyCategories.push({
              id: `legacy-${categoryLower}`,
              name: noteCategory,
              color: generateColorFromString(noteCategory),
              icon: "fa-file",
              order: categories.length + legacyCategories.length,
              isDefault: false,
              createdAt: new Date()
            });
          }
        }
      });

      if (legacyCategories.length > 0) {
        const updatedCategories = [...categories, ...legacyCategories];
        setCategories(updatedCategories);
        await saveCategoriesToFirestore(userId, updatedCategories);
      }
    } catch (error) {
      console.error("Error migrating categories:", error);
    }
  };

  const generateColorFromString = (str: string): string => {
    // Generate a deterministic color from a string
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 65%, 55%)`;
  };

  const saveCategoriesToFirestore = async (userId: string, categoriesToSave: Category[]) => {
    try {
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, {
        customCategories: categoriesToSave
      });
    } catch (error) {
      console.error("Error saving categories to Firestore:", error);
      throw error;
    }
  };

  const addCategory = async (category: Omit<Category, 'id' | 'createdAt'>) => {
    const userId = userDetails?.user?.uid;
    if (!userId) return;

    const newCategory: Category = {
      ...category,
      id: uuidv4(),
      createdAt: new Date()
    };

    const updatedCategories = [...categories, newCategory].sort((a, b) => a.order - b.order);
    setCategories(updatedCategories);
    await saveCategoriesToFirestore(userId, updatedCategories);
  };

  const updateCategory = async (id: string, updates: Partial<Category>) => {
    const userId = userDetails?.user?.uid;
    if (!userId) return;

    const updatedCategories = categories.map(cat =>
      cat.id === id ? { ...cat, ...updates } : cat
    );
    setCategories(updatedCategories);
    await saveCategoriesToFirestore(userId, updatedCategories);
  };

  const deleteCategory = async (id: string) => {
    const userId = userDetails?.user?.uid;
    if (!userId) return;

    // Don't allow deleting the last category
    if (categories.length === 1) {
      alert("You must have at least one category.");
      return;
    }

    const categoryToDelete = categories.find(c => c.id === id);
    if (!categoryToDelete) return;

    // Reassign notes using this category to "Other"
    try {
      const notesQuery = query(
        collection(db, "notes"),
        where("userId", "==", userId),
        where("category", "==", id)
      );
      const notesSnapshot = await getDocs(notesQuery);

      const fallbackCategory = categories.find(c => c.id === "note") || categories[0];
      const updatePromises = notesSnapshot.docs.map(noteDoc =>
        updateDoc(doc(db, "notes", noteDoc.id), {
          category: fallbackCategory?.id || "note"
        })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error reassigning notes:", error);
    }

    const updatedCategories = categories.filter(cat => cat.id !== id);
    setCategories(updatedCategories);
    await saveCategoriesToFirestore(userId, updatedCategories);
  };

  const getCategoryById = useCallback((id: string): Category | undefined => {
    return categories.find(c => c.id === id);
  }, [categories]);

  const getCategoryByName = useCallback((name: string): Category | undefined => {
    return categories.find(c => c.name.toLowerCase() === name.toLowerCase());
  }, [categories]);

  const reorderCategories = async (categoryIds: string[]) => {
    const userId = userDetails?.user?.uid;
    if (!userId) return;

    const reordered = categoryIds.map((id, index) => {
      const category = categories.find(c => c.id === id);
      return category ? { ...category, order: index } : null;
    }).filter(Boolean) as Category[];

    setCategories(reordered);
    await saveCategoriesToFirestore(userId, reordered);
  };

  const contextValue = useMemo(() => ({
    categories,
    addCategory,
    updateCategory,
    deleteCategory,
    getCategoryById,
    getCategoryByName,
    reorderCategories,
    isLoading,
  }), [categories, addCategory, updateCategory, deleteCategory, getCategoryById, getCategoryByName, reorderCategories, isLoading]);

  return (
    <CategoryContext.Provider value={contextValue}>
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategories = (): CategoryContextValue => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error("useCategories must be used within CategoryProvider");
  }
  return context;
};
